<template lang="pug">
modal.file-loader-modal(
  :aux-button-label='$t("modal.file_loader.select_files")'
  :aux-button-show='hasLoadedFiles'
  :close-button-label='$t("button.cancel")'
  :disableProcessButton='!hasSelectedFiles'
  :process-button-label='$t("button.send")'
  :title='$t("modal.file_loader.title")'
  @auxButtonAction='selectFile'
  @closed='onCloseModal'
  @opened='onOpenModal'
  @process='submit'
  ref='modal'
  size='lg'
)
  ul.nav.nav-tabs(role='tablist')
    li.nav-item(role='presentation')
      button.nav-link.active(
        aria-controls='settings'
        aria-selected='true'
        data-bs-target='#computer'
        data-bs-toggle='tab'
        role='tab'
        type='button'
      )
        icon(name='laptop')
        span.text.ms-2 {{ $t('modal.file_loader.computer') }}
    li.nav-item(
      role='presentation'
      v-if='canAccessStorageMenu && sourceStorage'
    )
      button.nav-link(
        aria-controls='storage'
        aria-selected='true'
        data-bs-target='#storage'
        data-bs-toggle='tab'
        role='tab'
        type='button'
      )
        icon(name='box-open')
        span.text.ms-2 {{ $t('modal.file_loader.storage') }}
  .tab-content
    #computer.tab-pane.fade.show.active(
      aria-labelledby='computer-tab'
      role='tab-computer'
    )
      .text-center.pt-3
        input(
          :accept='allowedFileType.join()'
          @change='loadFile'
          multiple='multiple'
          ref='inputFile'
          style='display: none'
          type='file'
        )
        .loaded-files-list(v-if='hasLoadedFiles || hasInvalidFiles')
          .row
            .col-12.col-md-6(
              :key='payload.index'
              v-for='(payload, index) in [].concat(computerFiles, invalidFiles)'
            )
              .loaded-files-list__item
                .content
                  .name {{ payload.file.name }}
                  button.d-flex.p-0.content-center(
                    :title='$t("button.remove")'
                    @click='removeFile(payload)'
                  )
                    icon.icon.m-0(name='trash-can' style='color: #949494')
                .file-error-message(
                  v-if='payload.invalidFile === "INVALID_FILE_TYPE"'
                )
                  span(
                    v-if='payload.invalidFile === "INVALID_FILE_TYPE"'
                    v-text='$t("modal.file_loader.invalid_file_type", { allowedFileExtensions: formattedAllowedFileExtensions })'
                  )
                .file-error-message(
                  v-if='payload.invalidFile === "INVALID_FILE_SIZE"'
                )
                  span.text {{ $t('modal.file_loader.invalid_file_size', { maxFileSizeMb }) }}

        .card--upload-file(v-else)
          div(
            :class='["dropzone-area", dragging ? "dragenterClass" : ""]'
            @dragend='dragging = false'
            @dragenter='dragging = true'
            @dragleave='dragging = false'
            @dragover.prevent='dragover'
            @drop.prevent='drop'
          )
            label.notclickable.block.cursor-pointer(for='assetsFieldHandle')
              h3.card-title(v-if='isBulkSend') {{ $t('configureBulkSend.recipients.upload.dragHere') }}
              h3.card-title(v-else) {{ $t('modal.file_loader.drag_here') }}
              p.font-size-subtitle.mb-2 {{ $t('modal.file_loader.or') }}
              button.btn.btn-thin(@click='selectFile') {{ $t('modal.file_loader.select_files') }}
              p.font-size-small.mb-0 {{ $t('modal.envelope.selectDocuments.maxFileSize', { allowedFileExtensions: allowedFileExtensions, maxFileSizeMb }) }}
              p.font-size-small.mb-0(v-if='isBulkSend') {{ $t('configureBulkSend.recipients.upload.maxSizePerUpload', { maxUploadSizeMB: maxUploadSizeMB }) }}
              p.font-size-small.mb-0(v-else) {{ $t('modal.envelope.selectDocuments.maxSizePerEnvelope', { maxSizePerEnvMB: envelopeSizeLimitMB }) }}
              p.font-size-small.mb-0(v-if='isBulkSend') {{ $t('configureBulkSend.recipients.upload.maxFilesPerUpload', { maxFilesPerUpload }) }}
              p.font-size-small.mb-0(v-else) {{ $t('modal.envelope.selectDocuments.maxFilesPerEnvelope', { maxFilesPerEnvelope: filesPerEnvelopeLimit }) }}
              p.font-size-small.mb-0(v-if='isBulkSend') {{ $t('configureBulkSend.recipients.upload.maxCsvFileRowsLimit', { maxCsvFileRowsLimit: maxFileRowsLimit }) }}
    #storage.tab-pane.fade.show(
      aria-labelledby='storage-tab'
      role='tab-storage'
    )
      files-list-mini.mt-3(
        :ignore-signed-items='true'
        :key='filesListKey'
        @change='selectedFiles'
      )
</template>
<script>
import FileValidator from '@/services/FileValidator';
import Icon from '@/ui/atoms/Icon.vue';
import Alert from '@/ui/molecules/Alert.vue';
import Modal from '@/ui/molecules/Modal.vue';
import FilesListMini from '@/ui/organisms/FilesListMini.vue';
import ToastAlert from '@/modules/account/common/alert';
import PermissionsService from '@/services/PermissionsService';
import ApiPermissions from '@/enums/ApiPermissions';

export default {
  components: {
    Icon,
    Alert,
    Modal,
    FilesListMini,
  },
  props: {
    folderId: String,
    sourceStorage: {
      type: Boolean,
      default: true,
    },
    maxFileSizeMb: {
      type: Number,
      default: 5,
    },
    allowedFileType: {
      type: Array,
      default: () => ['application/pdf'],
    },
    allowedFileExtensions: {
      type: Array,
      default: () => ['pdf'],
    },
    maxFileRowsLimit: {
      type: Number,
      default: 10000,
    },
    dragOnDrop: Boolean,
    isBulkSend: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      filesListKey: 0,
      computerFiles: [],
      invalidFiles: [],
      storageFiles: [],
      dragAndDropModal: false,
      dragging: false,
    };
  },
  computed: {
    canAccessStorageMenu() {
      return PermissionsService.can(
        this.$root.user,
        ApiPermissions.STORAGE_VIEW
      );
    },
    sanitizedComputerFiles() {
      if (this.computerFiles.length === 0) {
        return [];
      }
      return this.computerFiles
        .filter((payload) => !(payload.file instanceof File))
        .map((payload) => payload.file);
    },
    hasLoadedFiles() {
      return this.computerFiles.length > 0;
    },
    hasInvalidFiles() {
      return this.invalidFiles.length > 0;
    },
    hasStorageFiles() {
      return this.storageFiles.length > 0;
    },
    hasUploadedFiles() {
      if (!Array.isArray(this.sanitizedComputerFiles)) {
        return false;
      }
      return this.sanitizedComputerFiles.length > 0;
    },
    hasSelectedFiles() {
      return this.hasLoadedFiles || this.hasStorageFiles;
    },
    getSelectedFiles() {
      return this.storageFiles.concat(this.computerFiles);
    },
    formattedAllowedFileExtensions() {
      return this.allowedFileExtensions.join(', ');
    },
    filesPerEnvelopeLimit() {
      return (
        this.$store.getters['envelopeConfig/documentsPerEnvelopeLimit'] || 25
      );
    },
    envelopeSizeLimitMB() {
      return (
        this.$store.getters['envelopeConfig/documentsPerEnvelopeSizeLimitMB'] ||
        100
      );
    },
    maxUploadSizeMB() {
      return (
        this.$store.getters['envelopeConfig/bulkSendMaxUploadSizeMB'] || 25
      );
    },
    maxFilesPerUpload() {
      return 1;
    },
  },
  methods: {
    submit() {
      if (this.filesPerEnvelopeLimit < this.getSelectedFiles.length) {
        ToastAlert.fireAlert(
          this.$t('modal.envelope.maxFilesPerEnvelopeError', {
            maxFilesPerEnvelope: this.filesPerEnvelopeLimit,
          }),
          {
            classes: 'alert-warning',
            styles:
              'background-color: #FFFCE2; border-color: #EBE5A7; color: #635B00;',
            tag: 'httpAlert',
            icon: 'circle-exclamation',
          }
        );
        return;
      }
      this.$emit('selected-files', this.getSelectedFiles);
      this.$refs.modal.close();
    },
    onCloseModal() {
      this.computerFiles = [];
      this.storageFiles = [];
      this.filesListKey += 1;
    },
    onOpenModal() {
      this.$refs.inputFile.value = '';
      this.computerFiles = [];
      this.storageFiles = [];
    },
    async loadFile(e, dragOnDrop = false) {
      let recivedFiles;
      if (dragOnDrop) {
        recivedFiles = e.dataTransfer.files;
      } else {
        recivedFiles = e.target.files;
      }
      recivedFiles.forEach((file) => {
        const invalidFile = FileValidator.isInvalidFile(
          file,
          this.maxFileSizeMb,
          this.allowedFileType,
          this.allowedFileExtensions
        );
        if (invalidFile) {
          this.invalidFiles.push({
            index: new Date().getTime() + Math.random(),
            file,
            invalidFile,
          });
          return;
        }
        this.computerFiles.push({
          index: new Date().getTime() + Math.random(),
          file,
          invalidFile,
        });
      });
    },
    selectFile() {
      this.$refs.inputFile.click();
    },
    selectedFiles(files) {
      this.storageFiles = files;
    },
    dragover(event) {
      event.preventDefault();
    },
    drop(event) {
      event.preventDefault();
      this.dragAndDropModal = true;
      this.loadFile(event, true);
      this.dragging = false;
    },
    removeFile(payload) {
      if (payload.invalidFile) {
        this.invalidFiles = this.invalidFiles.filter(
          (f) => f.index !== payload.index
        );
        return;
      }
      this.computerFiles = this.computerFiles.filter(
        (f) => f.index !== payload.index
      );
    },
  },
};
</script>
<style lang="stylus">
.file-loader-modal
  .nav-tabs
    .nav-item
      .text
        margin-left: .5em
  .loaded-files-list__item
    border: 1px solid #ffeae9
    border-radius: 3px
    margin: 10px 0
    &:hover
      box-shadow: 0px 3px 5px rgba(0, 0, 0, .07)
    .content
      display: flex
      justify-content: space-between
      align-items: center
      padding: 1rem
      .name
        white-space: nowrap
        overflow: hidden
        text-overflow: ellipsis
        font-size: .875rem
    .file-error-message
      background-color: #ffeae9
      padding: 0.2rem 1rem
      font-size: 0.7em
      color: #d00a00
      text-align: left
  .card--upload-file
    button
      margin-bottom: .5rem
    .dragenterClass
      cursor: grabbing
      cursor: -webkit-grabbing
      background:rgba(0,0,0,0.1)
      .notclickable
        pointer-events: none;
        h3
          pointer-events: none;
        p
          pointer-events: none;
</style>
